
import { useState, useCallback } from 'react';
import { Message } from '@/types/chat';
import { useToast } from '@/hooks/use-toast';
import { extractBusinessData } from '@/services/businessDataExtractor';
import { createDynamicNotionPage } from '@/services/notion/notionPageService';
import { getOrCreateVisitorId } from '@/utils/cookieUtils';
import { triggerNotionCloneWebhook } from '@/services/notion/webhookService';

export const useNotionPlan = (messages: Message[], setMessages: (msgs: Message[]) => void) => {
  const [notionUrl, setNotionUrl] = useState<string | null>(null);
  const [businessData, setBusinessData] = useState({
    businessName: '',
    businessType: '',
    targetAudience: '',
    uniqueSellingPoints: []
  });
  
  const { toast } = useToast();
  const openAIApiKey = import.meta.env.VITE_OPENAI_API_KEY || '';

  const generateNotionPlan = async () => {
    console.log('Generating Notion plan automatically...');
    
    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));
      
      const data = await extractBusinessData(conversationHistory, openAIApiKey);
      const extractedData = {
        businessName: data.businessName || 'Your Business',
        businessType: data.businessType || 'General',
        targetAudience: data.targetAudience || 'General audience',
        uniqueSellingPoints: data.uniqueSellingPoints?.length > 0 
          ? data.uniqueSellingPoints 
          : ['Customized for your specific needs']
      };
      
      setBusinessData(extractedData);
      
      const userId = getOrCreateVisitorId();
      const contactInfo = {
        contactName: userId,
        contactEmail: `${userId}@example.com`,
      };
      
      // First, add a processing message to the chat
      const processingMessage: Message = {
        sender: 'ai',
        text: "I'm creating your custom Notion business plan based on our conversation. This will just take a moment..."
      };
      
      // Create a new messages array with the processing message
      let updatedMessages = [...messages, processingMessage];
      setMessages(updatedMessages);
      
      // Store the index of the processing message
      const processingMessageIndex = updatedMessages.length - 1;
      
      // First try to get URL directly from the webhook
      const chatContext = conversationHistory
        .map(msg => `${msg.role === 'user' ? 'Customer' : 'AI'}: ${msg.content}`)
        .join('\n\n');
      
      console.log('Calling webhook directly to get URL...');
      const webhookUrl = await triggerNotionCloneWebhook(userId, chatContext);
      console.log('Direct webhook URL result:', webhookUrl);
      
      let pageUrl = '';
      
      if (webhookUrl) {
        // If we got a URL directly from the webhook, use it
        console.log('Using URL directly from webhook:', webhookUrl);
        pageUrl = webhookUrl;
        setNotionUrl(webhookUrl);
      } else {
        // Fallback to the full page creation process
        console.log('No direct URL from webhook, using full creation process...');
        const result = await createDynamicNotionPage(
          conversationHistory,
          extractedData,
          contactInfo
        );
        
        console.log('Notion page creation result:', result);
        
        if (result.pageUrl) {
          pageUrl = result.pageUrl;
          setNotionUrl(pageUrl);
        } else {
          pageUrl = `https://www.notion.so/Knowledge-1d2e21e1f5118044b82ce32c8f247af7?pvs=4&v=${Date.now()}`;
          setNotionUrl(pageUrl);
        }
      }
      
      // Log the final URL we're using
      console.log('Final Notion URL to display:', pageUrl);
      
      // Format the message text to include a clickable link
      const notionLinkText = `<a href="${pageUrl}" target="_blank" rel="noopener noreferrer" class="text-goldcore underline">View your Notion plan</a>`;
      
      // Create the final message with the Notion plan link
      const newAiMessage: Message = {
        sender: 'ai',
        text: `Great! I've created your custom Notion business plan based on our conversation. You can access it here: ${notionLinkText}\n\n---\n\n**The plan includes strategies based on your unique selling points and target audience.** Is there anything specific from the plan you'd like me to explain further?`
      };
      
      // Replace the processing message with the final message
      updatedMessages = [
        ...updatedMessages.slice(0, processingMessageIndex),
        newAiMessage,
        ...updatedMessages.slice(processingMessageIndex + 1)
      ];
      setMessages(updatedMessages);
      
      toast({
        title: "Success!",
        description: "Your custom Notion business plan has been created.",
      });
      
      return pageUrl; // Ensure we return the URL
    } catch (error) {
      console.error('Error in Notion plan creation:', error);
      
      // Create a new error message
      const errorMessage: Message = {
        sender: 'ai',
        text: "I apologize, but I encountered an issue while creating your plan. Let me help you with a different approach."
      };
      
      // Add the error message to the chat
      setMessages([...messages, errorMessage]);
      
      toast({
        title: "Error",
        description: "There was an issue creating your plan. Please try again.",
        variant: "destructive"
      });
      
      return null;
    }
  };

  return {
    notionUrl,
    businessData,
    generateNotionPlan
  };
};
